import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Home, Settings, LogOut, LogIn, Leaf, Menu, X, KeyRound, Eye, EyeOff, Code } from 'lucide-react'
import { supabase } from '../lib/supabase'

function Navbar({ user, isAdmin }) {
  const [isOpen, setIsOpen] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })
  const location = useLocation()
  const navigate = useNavigate()

  const handleLogout = async () => {
    await supabase.auth.signOut()
    navigate('/')
  }

  const handlePasswordChange = async (e) => {
    e.preventDefault()
    setMessage({ type: '', text: '' })

    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: '密码长度至少6位' })
      return
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: '两次输入的密码不一致' })
      return
    }

    setLoading(true)
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword })
      if (error) throw error
      setMessage({ type: 'success', text: '密码修改成功！' })
      setTimeout(() => {
        setShowPasswordModal(false)
        setNewPassword('')
        setConfirmPassword('')
        setMessage({ type: '', text: '' })
      }, 1500)
    } catch (error) {
      setMessage({ type: 'error', text: error.message || '密码修改失败' })
    } finally {
      setLoading(false)
    }
  }

  const closePasswordModal = () => {
    setShowPasswordModal(false)
    setNewPassword('')
    setConfirmPassword('')
    setMessage({ type: '', text: '' })
  }

  const isActive = (path) => location.pathname === path

  const PasswordModal = () => (
    <div 
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) closePasswordModal()
      }}
    >
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 relative">
        <button
          onClick={closePasswordModal}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-2 transition-all duration-300"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-md shadow-green-300/30">
            <KeyRound className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">修改密码</h3>
            <p className="text-sm text-gray-500">{user?.email}</p>
          </div>
        </div>

        <form onSubmit={handlePasswordChange} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">新密码</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 border-2 border-green-200 rounded-xl focus:border-green-400 focus:ring-4 focus:ring-green-100 transition-all duration-300 outline-none"
                placeholder="请输入新密码"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">确认密码</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 border-2 border-green-200 rounded-xl focus:border-green-400 focus:ring-4 focus:ring-green-100 transition-all duration-300 outline-none"
                placeholder="请再次输入新密码"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {message.text && (
            <div className={`p-3 rounded-xl text-sm ${
              message.type === 'error' 
                ? 'bg-red-50 text-red-600 border border-red-200' 
                : 'bg-green-50 text-green-600 border border-green-200'
            }`}>
              {message.text}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={closePasswordModal}
              className="flex-1 px-6 py-3 border-2 border-gray-200 text-gray-600 rounded-xl font-medium hover:bg-gray-50 hover:border-gray-300 transition-all duration-300"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-green-400 to-emerald-500 text-white rounded-xl font-medium shadow-md shadow-green-300/30 hover:shadow-lg hover:shadow-green-300/40 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? '修改中...' : '确认修改'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )

  return (
    <>
      <nav className="bg-white/70 backdrop-blur-md border-b border-green-200/60 sticky top-0 z-40 shadow-sm">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-11 h-11 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-md shadow-green-300/30 transition-all duration-300 group-hover:shadow-lg group-hover:shadow-green-300/40 group-hover:scale-105">
                <Leaf className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-green-800">烟草查询</span>
            </Link>

            <div className="hidden md:flex items-center gap-3">
              <Link
                to="/"
                className={`px-5 py-2.5 rounded-full font-medium transition-all duration-300 ${
                  isActive('/') 
                    ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 shadow-sm' 
                    : 'text-gray-600 hover:bg-green-50 hover:text-green-600'
                }`}
              >
                <Home className="w-4 h-4 inline mr-2" />
                首页
              </Link>
              <Link
                to="/api"
                className={`px-5 py-2.5 rounded-full font-medium transition-all duration-300 ${
                  isActive('/api') 
                    ? 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 shadow-sm' 
                    : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                }`}
              >
                <Code className="w-4 h-4 inline mr-2" />
                API
              </Link>
              {user ? (
                <>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className={`px-5 py-2.5 rounded-full font-medium transition-all duration-300 ${
                        isActive('/admin') 
                          ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 shadow-sm' 
                          : 'text-gray-600 hover:bg-green-50 hover:text-green-600'
                      }`}
                    >
                      <Settings className="w-4 h-4 inline mr-2" />
                      管理
                    </Link>
                  )}
                  <div className="h-6 w-px bg-green-200/60"></div>
                  <button
                    onClick={() => setShowPasswordModal(true)}
                    className="text-sm text-gray-500 hover:text-green-600 px-2 py-1 rounded-full hover:bg-green-50 transition-all duration-300 cursor-pointer"
                    title="点击修改密码"
                  >
                    {user.email}
                  </button>
                  <button
                    onClick={handleLogout}
                    className="px-5 py-2.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-all duration-300 font-medium"
                  >
                    <LogOut className="w-4 h-4 inline mr-2" />
                    退出
                  </button>
                </>
              ) : (
                <Link
                  to="/auth"
                  className="bg-gradient-to-r from-green-400 to-emerald-500 text-white px-6 py-2.5 rounded-full font-medium shadow-md shadow-green-300/30 transition-all duration-300 hover:shadow-lg hover:shadow-green-300/40 hover:scale-105"
                >
                  <LogIn className="w-4 h-4 inline mr-2" />
                  登录
                </Link>
              )}
            </div>

            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden p-2.5 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-full"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {isOpen && (
            <div className="md:hidden py-4 border-t border-green-100">
              <div className="space-y-2">
                <Link
                  to="/"
                  onClick={() => setIsOpen(false)}
                  className={`block px-5 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    isActive('/') ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700' : 'text-gray-600 hover:bg-green-50'
                  }`}
                >
                  <Home className="w-4 h-4 inline mr-2" />
                  首页
                </Link>
                <Link
                  to="/api"
                  onClick={() => setIsOpen(false)}
                  className={`block px-5 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    isActive('/api') ? 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700' : 'text-gray-600 hover:bg-blue-50'
                  }`}
                >
                  <Code className="w-4 h-4 inline mr-2" />
                  API
                </Link>
                {user ? (
                  <>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        onClick={() => setIsOpen(false)}
                        className={`block px-5 py-3 rounded-2xl font-medium transition-all duration-300 ${
                          isActive('/admin') ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700' : 'text-gray-600 hover:bg-green-50'
                        }`}
                      >
                        <Settings className="w-4 h-4 inline mr-2" />
                        管理
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        setIsOpen(false)
                        setShowPasswordModal(true)
                      }}
                      className="block w-full text-left px-5 py-2 text-sm text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-2xl transition-all duration-300"
                    >
                      {user.email}
                    </button>
                    <button
                      onClick={() => {
                        handleLogout()
                        setIsOpen(false)
                      }}
                      className="block w-full text-left px-5 py-3 text-red-600 hover:bg-red-50 rounded-2xl font-medium transition-all duration-300"
                    >
                      <LogOut className="w-4 h-4 inline mr-2" />
                      退出登录
                    </button>
                  </>
                ) : (
                  <Link
                    to="/auth"
                    onClick={() => setIsOpen(false)}
                    className="block px-5 py-3 bg-gradient-to-r from-green-400 to-emerald-500 text-white rounded-2xl font-medium text-center shadow-md shadow-green-300/30"
                  >
                    <LogIn className="w-4 h-4 inline mr-2" />
                    登录
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {showPasswordModal && createPortal(<PasswordModal />, document.body)}
    </>
  )
}

export default Navbar
